import { isPlatformSideNav } from '@unifyapps/carbon/components/NavigationContainer/helpers';
import { Box } from '@unifyapps/ui/components/Box';
import type { TooltipModes } from '@unifyapps/ui/components/Tooltip';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { Typography } from '@unifyapps/ui/components/Typography';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { clsx } from 'clsx';
import _noop from 'lodash/noop';
import type { CSSProperties, PropsWithChildren, ReactElement } from 'react';
import { Children, useCallback } from 'react';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { TooltipWithShortcut } from '../../../TooltipWithShortcut';
import { NavIconPair } from '../../components/NavIconPair';
import type { NavigationItemSlotPropsType, NotificationActionPropsType } from '../../types';
import { NavigationItemLabelVariantEnum, NavigationItemSelectionVariantEnum } from '../../types';
import { getActionNavItemClassNames } from './getActionNavItemClassNames';
import { getActionNavItemContainerClassNames } from './getActionNavItemContainerClassNames';

const TOOLTIP_OFFSET = 6;

type SlotProps = { name: string; id?: string };

function Slot(_props: PropsWithChildren<SlotProps>): null {
  return null;
}

type BaseActionNavItemProps = {
  children?: ReactElement | null;
  id: string;
  isActive?: boolean;
  icon?: string;
  iconClassName?: string;
  activeIcon?: string;
  endIcon?: string;
  activeEndIcon?: string;
  name: string;
  onClick: (args: { onOpen: () => void; onClose: () => void }) => void;
  variant: 'lite' | 'rich';
  onOpenChange?: (id: string, open: boolean) => void;
  slotProps?: NavigationItemSlotPropsType;
  actionProps?: NotificationActionPropsType;
  action?: string;
  renderEndDecorator?: () => React.ReactNode;
  shortcutKeys?: string;
  disabled?: boolean;
};

const DISABLED_STYLE = { backgroundColor: 'transparent' };

export default function BaseActionNavItem({
  isActive = false,
  icon,
  activeIcon,
  endIcon,
  activeEndIcon,
  name,
  onClick,
  variant,
  onOpenChange,
  id,
  slotProps,
  actionProps,
  children,
  disabled,
  shortcutKeys,
  renderEndDecorator,
  iconClassName: iconClassNameProp,
}: BaseActionNavItemProps) {
  const childrenArr = Children.toArray(children);
  const badgeSlot = childrenArr.find(
    (child: ReactElement<SlotProps>) => child.props.name === 'badge',
  ) as ReactElement<{ children: ReactElement }> | undefined;

  const { navigationItem, container } = slotProps as unknown as NavigationItemSlotPropsType;
  const {
    expanded,
    labelVariant,
    selectionVariant,
    typographyClassName,
    navItemClassName,
    navItemStyles,
    hideActionNavLabel,
    navItemBorderRadiusStyles,
  } = navigationItem ?? {};

  const _isPlatformSideNav = isPlatformSideNav(labelVariant);

  const { rootClassName, iconClassname, labelClassname, isHorizontalUpfront } =
    getActionNavItemClassNames({
      isActive,
      icon,
      variant,
      disabled,
      navigationItem,
      isPlatformSideNav: _isPlatformSideNav,
    });

  const {
    navIconContainerClassName,
    iconStateClassName,
    containerClassName,
    isClickOnIconContainer,
  } = getActionNavItemContainerClassNames({
    isActive,
    variant,
    disabled,
    navigationItem,
  });

  const count = actionProps?.unopenedNotificationsCount ?? 0;
  // a non-zero error count (DevTools) reddens the whole item — icon + button — via the icon's own
  // danger color scheme, not just the badge. Gated on actionProps.isError so neutral counts stay as-is.
  const isErrorActive = count > 0 && actionProps?.isError === true;

  const handleClick = useCallback(() => {
    if (disabled) return _noop;
    onClick({
      onOpen: () => onOpenChange?.(id, true),
      onClose: () => onOpenChange?.(id, false),
    });
  }, [disabled, onClick, onOpenChange, id]);

  const scheme = slotProps?.theme as unknown as TooltipModes | undefined;

  const sharedIconProps = {
    className: iconClassname,
    disabled,
    iconClassName: iconStateClassName,
    isActive,
    isError: isErrorActive,
    isHorizontalUpfront,
    label: name,
    selectionVariant,
  };

  const iconEl = icon ? (
    <Box
      className={clsx('relative', navIconContainerClassName)}
      {...(isClickOnIconContainer ? { onClick: handleClick } : {})}
      style={disabled ? DISABLED_STYLE : (EMPTY_OBJECT as CSSProperties)}
    >
      <NavIconPair
        {...sharedIconProps}
        activeIconName={activeIcon}
        iconClassName={clsx(sharedIconProps.iconClassName, iconClassNameProp)}
        iconName={icon}
      />
      {count > 0 && !expanded ? (
        <Box
          className={clsx('absolute', {
            'start-[6px] -top-[12px]': _isPlatformSideNav,
            '-end-3 -top-4':
              !_isPlatformSideNav && selectionVariant !== NavigationItemSelectionVariantEnum.Solid,
            '-end-1 -top-2':
              !_isPlatformSideNav && selectionVariant === NavigationItemSelectionVariantEnum.Solid,
          })}
        >
          {badgeSlot?.props.children}
        </Box>
      ) : null}
    </Box>
  ) : null;

  const endIconEl =
    endIcon && expanded !== false ? (
      <Box className={clsx('relative ms-auto', navIconContainerClassName)}>
        <NavIconPair {...sharedIconProps} activeIconName={activeEndIcon} iconName={endIcon} />
      </Box>
    ) : null;

  const el = (
    <Box
      as={selectionVariant === NavigationItemSelectionVariantEnum.Solid ? 'div' : 'button'}
      className={slowCn(rootClassName, navItemClassName)}
      data-label-variant={labelVariant}
      style={navItemStyles}
      {...(isClickOnIconContainer ? {} : { onClick: handleClick })}
    >
      {labelVariant === NavigationItemLabelVariantEnum.Tooltip &&
      selectionVariant === NavigationItemSelectionVariantEnum.Solid &&
      iconEl ? (
        // Show tooltip only on Icon
        <TooltipWithShortcut
          arrow={false}
          disabled={disabled}
          mode={scheme}
          offset={TOOLTIP_OFFSET}
          placement="right"
          shortcutKeys={shortcutKeys}
          title={name}
        >
          {iconEl}
        </TooltipWithShortcut>
      ) : (
        iconEl
      )}

      {(expanded ||
        labelVariant === NavigationItemLabelVariantEnum.Upfront ||
        variant === 'lite') &&
      !hideActionNavLabel ? (
        <Box
          className={clsx(
            // min-w-0 lets this flex child shrink below the label's intrinsic width so the
            // Typography's `truncate` can engage; without it long names push the row past
            // the rail and clip any end decorator (e.g. pills) behind a horizontal scroll.
            'relative inline-flex w-full min-w-0 items-center',
            {
              '!justify-start': variant === 'lite',
            },
            {
              'justify-center':
                labelVariant === NavigationItemLabelVariantEnum.Upfront && !isHorizontalUpfront,
            },
            { 'justify-between': expanded },
          )}
        >
          <Typography
            className={slowCn(labelClassname, typographyClassName)}
            {...(isClickOnIconContainer ? { onClick: handleClick } : {})}
          >
            {name}
          </Typography>
          {count > 0 && expanded ? (
            <Box className={clsx({ 'me-md': !_isPlatformSideNav })}>
              {badgeSlot?.props.children}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {endIconEl}
      {renderEndDecorator?.()}
    </Box>
  );

  const tooltipEl =
    labelVariant === NavigationItemLabelVariantEnum.Tooltip &&
    selectionVariant === NavigationItemSelectionVariantEnum.Stroke ? (
      <Tooltip arrow={false} mode={scheme} offset={TOOLTIP_OFFSET} placement="right" title={name}>
        {el}
      </Tooltip>
    ) : (
      el
    );

  return variant === 'lite' ? (
    <Box
      className={slowCn(containerClassName, container?.className)}
      onClick={handleClick}
      style={navItemBorderRadiusStyles}
    >
      {tooltipEl}
    </Box>
  ) : (
    tooltipEl
  );
}

BaseActionNavItem.Slot = Slot;
